import { Signal } from "signals";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import AnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/animate-symbol-behaviour";
import DimSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/dim-symbol-behaviour";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import Layers from "slotworks/graphics/layers/layers";
import { AnticipationInfo } from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import Contract from "slotworks/utils/contract";
import Point from "slotworks/utils/geom/point";
import Timer from "slotworks/utils/timer";
import deviceInfo from "slotworks/utils/device-info";
import { off } from "hammerjs";
import { Graphics } from "pixi.js";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";

class MatrixComponent {

    public readonly startSignal: Signal;
    public readonly landSignal: Signal;

    public stickyLayer: Layers;

    private reelTransition: AbstractReelTransition;
    private reels: ReelComponent[];

    private stuckSymbols: Map<string, SymbolComponent> = new Map<string, SymbolComponent>();

    /**
     * Array of symbols used only for transitions (ie reel spins)
     */
    private hiddenSymbols: SymbolComponent[];

    private defaultStops: number[];
    private defaultReelset: string[][];

    private tickTimer: number;

    private startTime: number;

    constructor(grid: number[], stops: number[], reelset: string[][]) {

        this.startSignal = new Signal();
        this.landSignal = new Signal();

        this.defaultStops = stops;
        this.defaultReelset = reelset;

        this.hiddenSymbols = [];

        this.drawMask();

        // Create reels
        this.reels = [];

        for (let x = 0; x < grid.length; x++) {

            const reelstrip = reelset[x];

            const reel = new ReelComponent(x + 1, grid[x], reelstrip);
            reel.jump(stops[x], reelset[x]);

            this.reels.push(reel);
        }

        this.flush();
        // deviceInfo.onLayoutChange.add(this.resetOnOrientationChange, this);
        if(!deviceInfo.isDesktop)
            canvasManager.onOrientationChange.add(this.resetOnOrientationChange, this);
    }

    public reset() {
        this.jump(this.defaultStops, this.defaultReelset);
        this.stopTick();
        this.flush();
    }

    public getTransition() {
        return this.reelTransition;
    }

    public setTransition(reelTransition: AbstractReelTransition) {
        this.reelTransition = reelTransition;
        this.reelTransition.init(this.reels, this.defaultStops, this.defaultReelset);
        this.reelTransition.onReelLand.add(this.onReelLand);
    }

    public startTransition(jumpStart: boolean = false): Contract<void> {
        for (const reel of this.reels) {
            reel.spin().execute();
        }
        this.startTime = Date.now();
        this.startTick();

        this.startSignal.dispatch();

        return this.reelTransition.start(null, jumpStart);
    }

    public stopTransition(stops: number[], reelset: string[][], quickSpin: boolean, anticipation: AnticipationInfo): Contract<void> {
        return new Contract<void>((resolve) => {
            for (let reel = 0; reel < this.reels.length; reel++) {
                this.reels[reel].validLandSymbols = anticipation.landSymbols[reel];
            }

            this.reelTransition.stop(stops, reelset, quickSpin, anticipation).then(() => {
                this.postProcessReels();
                this.stopTick();
                resolve(null);
            });
        });
    }

    public skipTransition(stops: number[], reelset: string[][], anticipation: AnticipationInfo): Contract<void> {
        return new Contract<void>((resolve) => {
            for (let reel = 0; reel < this.reels.length; reel++) {
                this.reels[reel].validLandSymbols = anticipation.landSymbols[reel];
            }

            this.reelTransition.skip(stops, reelset).then(() => {
                this.postProcessReels();
                this.stopTick();
                resolve(null);
            });
        });
    }

    public jumpToGrid(grid: string[][]) {
        const stops = [];
        const reelset: string[][] = [];
        for (const reel of grid) {
            stops.push(0);
            reelset.push([...reel]);
        }

        this.jump(stops, reelset);
    }

    public jump(stops: number[], reelset: string[][]) {
        this.reelTransition.jump(stops, reelset);

        for (let i = 0; i < stops.length; i++) {
            this.reels[i].jump(stops[i], reelset[i]);
        }

        this.postProcessReels();
    }

    public getReels(): ReelComponent[] {
        return this.reels;
    }

    /**
     * Gets a symbol, (0 indexed)
     * For example getSymbol(4,0) will return the top right symbol in a 5x3 slot
     */
    public getSymbol(x: number, y: number): SymbolComponent {
        if (y < 0 || !this.reels[x] || !this.reels[x].getSymbolAt(y)) {
            // Symbol isn't in "standard" grid, so search hidden symbols
            for (const hiddenSymbol of this.hiddenSymbols) {
                if (hiddenSymbol.gridPosition.x === x + 1 && hiddenSymbol.gridPosition.y === y + 1) {
                    return hiddenSymbol;
                }
            }

            return null;
        }
        return this.reels[x].getSymbolAt(y);
    }

    /**
     * Gets all symbols, including sticky symbols
     */
    public getAllSymbols(): SymbolComponent[] {

        const symbols: SymbolComponent[] = this.getGridSymbols();

        if (this.stuckSymbols) {
            this.stuckSymbols.forEach((stuckSymbol) => {
                symbols.push(stuckSymbol);
            });
        }

        return symbols;
    }

    /**
     * Gets only the symbols on the natural grid, excluding things such as sticky symbols
     */
    public getGridSymbols(): SymbolComponent[] {

        let symbols: SymbolComponent[] = [];

        for (const reel of this.reels) {
            symbols = symbols.concat(reel.getSymbols());
        }

        return symbols;
    }

    public resetSymbolPositions() {
        this.drawMask();

        for (const reel of this.reels) {
            reel.resetSymbolPositions();
        }

        this.postProcessReels();
    }

    public addHiddenSymbol(symbol: SymbolComponent) {
        this.hiddenSymbols.push(symbol);
    }

    public removeHiddenSymbol(symbol: SymbolComponent) {
        for (let i = 0; i < this.hiddenSymbols.length; i++) {
            const hiddenSymbol = this.hiddenSymbols[i];
            if (hiddenSymbol === symbol) {
                this.hiddenSymbols.splice(i, 1);
                break;
            }
        }
    }

    public resetSymbols() {
        for (const symbol of this.getAllSymbols()) {
            symbol.setVisible(true);
            symbol.static();
        }

        this.postProcessReels();
    }

    public flush() {
        this.postProcessReels();
    }

    public addStuckSymbol(symbol: SymbolComponent) {
        this.addStuckSymbolById(symbol.gridPosition, symbol.symbolId);
    }

    public addStuckSymbolById(gridPosition: Point, symbolId: string) {
        const x = gridPosition.x;
        const y = gridPosition.y;
        const id = `symbol_${x}_${y}`;

        if (!this.stuckSymbols.has(id)) {
            const stickyWild = new SymbolComponent(gridPosition.clone());
            stickyWild.setSymbol(symbolId + "_stuck");

            const positionRect = Layers.MatrixContent.getPosition(id);

            stickyWild.setTransform(positionRect);
            stickyWild.setLayer(this.stickyLayer || Layers.SymbolAnimations);
            stickyWild.lockLayer();
            stickyWild.updateTransform();

            stickyWild.addBehaviour(new AnimateSymbolBehaviour(stickyWild));
            stickyWild.addBehaviour(new DimSymbolBehaviour(stickyWild));

            this.stuckSymbols.set(id, stickyWild);
        }
    }

    public getStuckSymbols() {
        return this.stuckSymbols;
    }

    public clearStuckSymbols() {
        this.stuckSymbols.forEach((symbol: SymbolComponent) => {
            symbol.destroy();
        });

        this.stuckSymbols.clear();
        this.resetSymbols();
    }

    public hold(reelIndexes?: number[]) {
        this.reelTransition.hold(reelIndexes);
    }

    public resetOnOrientationChange() {
        const mask:Graphics = Layers.MatrixContent.container.mask as Graphics;
        mask.clear();
        mask.beginFill(0xff0000);
        mask.drawRect(Layers.MatrixContent.bounds.x + canvasManager.stageOffsetX, Layers.MatrixContent.bounds.y + canvasManager.stageOffsetY, Layers.MatrixContent.bounds.width, Layers.MatrixContent.bounds.height);
        
        this.resetSymbolPositions();
    }

    public getStartTime() {
        return this.startTime;
    }

    private onReelLand = (reelIndex: number) => {
        const reel = this.reels[reelIndex - 1];
        reel.land().execute();

        this.landSignal.dispatch(reelIndex);

        this.postProcessReels();
    }

    private drawMask() {
        // Masking
        const mask = new PIXI.Graphics();
        mask.beginFill(0xff0000);
        mask.drawRect(Layers.MatrixContent.bounds.x + canvasManager.stageOffsetX, Layers.MatrixContent.bounds.y + canvasManager.stageOffsetY, Layers.MatrixContent.bounds.width, Layers.MatrixContent.bounds.height);

        Layers.MatrixContent.container.mask = mask;
    }

    private startTick() {
        this.stopTick();
        this.tickTimer = Timer.setInterval(this.tick, 0);
    }

    private stopTick() {
        Timer.clearInterval(this.tickTimer);
    }

    private tick = () => {
        this.reelTransition.update();

        this.postProcessReels();
    }

    private postProcessReels = () => {
        for (let x = 0; x < this.reels.length; x++) {
            const reel = this.reels[x];

            let currentStack = null;

            for (let y = -2; y < reel.size + 1; y++) {
                const symbol = this.getSymbol(x, y);
                if (!symbol) {
                    continue;
                }
                const symbolDef = SlotDefinition.getSymbolDefinition(symbol.symbolId);

                symbol.offsetStack(0);
                symbol.setIsStackPart(false);
                symbol.setProxy(null);

                if (currentStack && symbol.symbolId !== currentStack.symbolId) {
                    // Found end of stack
                    currentStack = null;
                }

                // Found the top of a stack
                if (!currentStack && symbolDef.size > 1) {
                    currentStack = symbol;
                    const topOfStack = y;

                    // Check if it's the true top or if it's only half in view
                    let visibleSize = 1;
                    for (let rowBelow = y + 1; rowBelow < Math.min(reel.size, y + symbolDef.size); rowBelow++) {
                        const symbolBelow = this.getSymbol(x, rowBelow);

                        if (symbolBelow.symbolId === symbol.symbolId) {
                            // Found more stack
                            visibleSize++;
                            // Hide stack piece and skip processing it
                            symbolBelow.setIsStackPart(true);
                            symbolBelow.setProxy(currentStack);
                            y++;
                        } else {
                            break;
                        }
                    }

                    // If whole thing isn't showing
                    if (visibleSize < symbolDef.size) {
                        // If it's not cut off at the bottom, it means it's cut off at the top, so we offset
                        if (topOfStack + visibleSize < reel.size) {
                            symbol.offsetStack(symbolDef.size - visibleSize);
                        }
                    }
                }

                // Sticky symbols
                if (!symbol.isMoving()) {
                    this.stuckSymbols.forEach((stuckSymbol: SymbolComponent) => {
                        if (stuckSymbol.gridPosition.x === symbol.gridPosition.x && stuckSymbol.gridPosition.y === symbol.gridPosition.y) {
                            symbol.setVisible(false);
                            symbol.setProxy(stuckSymbol);
                        }
                    });
                }

                symbol.updateTransform();
            }
        }
    }
}

export default MatrixComponent;



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/matrix/matrix-component.ts